"use client";

import Link from "next/link";

import { EnvelopeSimple, FacebookLogo, InstagramLogo, Phone, TwitterLogo } from "@phosphor-icons/react";
import { motion } from "motion/react";

const easeCubic = [0.32, 0.72, 0, 1] as const;

export function Footer() {
  return (
    <footer className="w-full bg-[#F9F9FB] px-4 py-32 border-t border-black/5 flex flex-col items-center justify-center overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 48, filter: "blur(8px)" }}
        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 1.2, ease: easeCubic }}
        className="flex flex-col items-center w-full max-w-4xl text-center space-y-16"
      >
        <div className="flex flex-col items-center space-y-4">
          <div className="rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-medium bg-black/5 text-black/60 mb-2">
            Contact
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A0A0A]">Let's Connect.</h2>
          <p
            className="text-lg md:text-xl text-black/50 font-medium max-w-md"
            style={{ fontFamily: "var(--font-geist-mono), monospace" }}
          >
            Reach out for elite support and premium partnerships.
          </p>
        </div>

        {/* Double-Bezel Contact Island */}
        <div className="w-full max-w-2xl rounded-[2.5rem] p-2 bg-black/5 ring-1 ring-black/5 shadow-sm">
          <div className="w-full rounded-[calc(2.5rem-0.5rem)] bg-white py-8 px-6 md:px-12 shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)] border border-black/5 flex flex-col md:flex-row items-center justify-between gap-8">
            <a
              href="mailto:vokiya.code@gmail.com"
              className="group flex flex-col md:flex-row items-center space-y-3 md:space-y-0 md:space-x-4 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:opacity-70 active:scale-[0.98]"
            >
              <div className="w-12 h-12 rounded-full bg-black/[0.03] flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:scale-110 group-hover:bg-black/[0.06]">
                <EnvelopeSimple weight="light" className="w-6 h-6 text-black" />
              </div>
              <span className="font-semibold text-black/80 tracking-wide text-sm md:text-base">
                vokiya.code@gmail.com
              </span>
            </a>

            <div className="hidden md:block w-[1px] h-12 bg-black/5" />
            <div className="md:hidden w-12 h-[1px] bg-black/5" />

            <a
              href="tel:+213540839434"
              className="group flex flex-col md:flex-row items-center space-y-3 md:space-y-0 md:space-x-4 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:opacity-70 active:scale-[0.98]"
            >
              <div className="w-12 h-12 rounded-full bg-black/[0.03] flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:scale-110 group-hover:bg-black/[0.06]">
                <Phone weight="light" className="w-6 h-6 text-black" />
              </div>
              <span className="font-semibold text-black/80 tracking-wide text-sm md:text-base">+213 540839434</span>
            </a>
          </div>
        </div>

        {/* Social Links - Fluid Island style */}
        <div className="flex gap-4">
          {[
            { icon: TwitterLogo, href: "#", label: "Twitter" },
            { icon: InstagramLogo, href: "#", label: "Instagram" },
            { icon: FacebookLogo, href: "#", label: "Facebook" },
          ].map((social, i) => (
            <a
              key={i}
              href={social.href}
              aria-label={social.label}
              className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-white border border-black/10 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] hover:bg-black/5 active:scale-[0.98] shadow-sm hover:shadow-md"
            >
              <social.icon
                weight="light"
                className="w-5 h-5 text-black transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:scale-110 group-hover:-translate-y-px"
              />
            </a>
          ))}
        </div>

        <div className="flex flex-col items-center gap-3 pt-8 font-medium text-black/30 text-sm tracking-wide sm:flex-row">
          <span>© {new Date().getFullYear()} Vokiya. All rights reserved.</span>
          <Link href="/auth/v1/login" className="text-black/45 transition-colors duration-300 hover:text-black/70">
            Login
          </Link>
        </div>
      </motion.div>
    </footer>
  );
}
